/**
 * Lightweight polyfill of the proposed `<rangegroup>` HTML element (Open UI
 * "enhanced range slider" explainer). Registered as `<range-group>` because an
 * autonomous custom element name must contain a hyphen, while the explainer
 * models the future native syntax as `<rangegroup>`.
 *
 * It enhances its light-DOM `<input type="range">` children into one coordinated
 * dual-thumb track (a shared rail with a filled segment between the two thumbs).
 * Crucially the thumbs ARE the real range inputs, kept in light DOM — so if the
 * element is never upgraded (no `customElements` support, or scripting off) the
 * control gracefully falls back to plain, still-functional stacked sliders that
 * submit and remain keyboard-accessible.
 *
 * Public API (mirrors the POC):
 * - `values`: the thumbs' current numeric values, in document order.
 * - `setRangeValue(index, value)`: programmatically move one thumb (fires
 *   `input` + `change`).
 *
 * Events: bubbling `input` (while a thumb moves) and `change` (on commit) — the
 * native events from the child inputs bubble through the host, and
 * `setRangeValue` re-dispatches them for typed updates.
 */
export class RangeGroupElement extends HTMLElement {
  private _inputs: HTMLInputElement[] = [];
  private _fill: HTMLDivElement | null = null;
  private _enhanced = false;

  connectedCallback() {
    if (this._enhanced) {
      this.paint();
      return;
    }
    const inputs = Array.from(
      this.querySelectorAll<HTMLInputElement>('input[type="range"]'),
    );
    // Nothing to coordinate yet (children not appended) — stay a plain wrapper.
    if (inputs.length < 2) return;
    this._enhanced = true;
    this._inputs = inputs;

    const rail = document.createElement("div");
    rail.className = "range-group-rail";
    const fill = document.createElement("div");
    fill.className = "range-group-fill";
    this._fill = fill;
    this.prepend(fill);
    this.prepend(rail);

    inputs.forEach((inp, i) => {
      // Stack later thumbs above earlier ones so an overlapping pair stays
      // individually grabbable.
      inp.style.zIndex = String(i + 1);
      inp.addEventListener("input", () => {
        this.enforceOrder(i);
        this.paint();
      });
    });
    this.paint();
  }

  get min(): number {
    return Number(this.getAttribute("min") ?? 0);
  }
  get max(): number {
    return Number(this.getAttribute("max") ?? 100);
  }
  get values(): number[] {
    return this._inputs.map((i) => Number(i.value));
  }

  /** Move thumb `index` to `value`, keeping the thumbs ordered, and notify. */
  setRangeValue(index: number, value: number) {
    const inp = this._inputs[index];
    if (!inp) return;
    inp.value = String(value);
    this.enforceOrder(index);
    this.paint();
    this.dispatchEvent(new Event("input", { bubbles: true }));
    this.dispatchEvent(new Event("change", { bubbles: true }));
  }

  /** Clamp the just-moved thumb so the sequence stays non-decreasing. */
  private enforceOrder(changed: number) {
    const v = this.values;
    const inp = this._inputs[changed];
    if (!inp) return;
    const prev = v[changed - 1];
    const next = v[changed + 1];
    if (changed > 0 && prev != null && v[changed]! < prev) {
      inp.value = String(prev);
    }
    if (
      changed < this._inputs.length - 1 &&
      next != null &&
      v[changed]! > next
    ) {
      inp.value = String(next);
    }
  }

  private paint() {
    if (!this._fill || this._inputs.length === 0) return;
    const v = this.values;
    const lo = Math.min(...v);
    const hi = Math.max(...v);
    const span = this.max - this.min || 1;
    const pct = (x: number) => ((x - this.min) / span) * 100;
    this._fill.style.left = `${pct(lo)}%`;
    this._fill.style.width = `${Math.max(0, pct(hi) - pct(lo))}%`;
  }
}

if (
  typeof customElements !== "undefined" &&
  !customElements.get("range-group")
) {
  customElements.define("range-group", RangeGroupElement);
}
